import React from 'react';
import moment from 'moment';
import { Badge } from './Badge';
import { AllocationRule, ResultVia } from '../../types';
import { IconButton, Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Route as RouteIcon,
  FilterList as FilterListIcon,
  AccessTime as AccessTimeIcon,
  Update as UpdateIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { Menu, MenuItem, Switch, FormControlLabel } from '@mui/material';

const shouldShowTooltip = (text: string | undefined | null): boolean => {
  return Boolean(text && text.trim().length > 0);
};

interface RuleCardProps {
  rule: AllocationRule;
  onEdit: (rule: AllocationRule) => void;
  onDelete: (ruleId: string) => void;
  onTest: (rule: AllocationRule) => void;
  onView: (rule: AllocationRule) => void;
  onToggle: (rule: AllocationRule) => void;
  isDeleting?: boolean;
}

export const RuleCard: React.FC<RuleCardProps> = ({ rule, onEdit, onDelete, onTest, onView, onToggle, isDeleting = false }) => {
  const disableActions = isDeleting;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const getResultViaVariant = (resultVia: ResultVia): 'partner' | 'internal' | 'courier' | 'warehouse' | 'default' => {
    const mapping: Record<ResultVia, 'partner' | 'internal' | 'courier' | 'warehouse' | 'default'> = {
      'Partner': 'partner',
      'Internal': 'internal',
      'Courier': 'courier',
      'InternalWarehouse': 'warehouse',
    };
    return mapping[resultVia] || 'default';
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    handleClose();
    onView(rule);
  };

  const handleEdit = () => {
    handleClose();
    onEdit(rule);
  };

  const handleDelete = () => {
    handleClose();
    onDelete(rule.id);
  };

  const handleTest = () => {
    handleClose();
    onTest(rule);
  };

  return (
    <div className="group rounded-lg border border-gray-300 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] transition-all duration-300 relative overflow-hidden flex flex-col h-full">
      <div className="p-5 flex-1 overflow-y-auto">
        <div className="flex items-start justify-between mb-1.5">
          {shouldShowTooltip(rule.name) ? (
            <Tooltip
              title={rule.name}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '0.75rem',
                    maxWidth: '200px',
                    padding: '8px 12px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  }
                },
                arrow: {
                  sx: {
                    color: 'white',
                  }
                }
              }}
            >
              <h3 className="text-sm font-bold text-gray-900 truncate leading-tight flex-1 pr-2 cursor-help min-w-0">
                <span className="text-xs font-mono text-gray-500 font-normal">ID: {rule.id}</span>
                <span className="mx-2 text-orange-500">|</span>
                {rule.name}
              </h3>
            </Tooltip>
          ) : (
            <h3 className="text-sm font-bold text-gray-900 truncate leading-tight flex-1 pr-2 min-w-0">
              <span className="text-xs font-mono text-gray-500 font-normal">ID: {rule.id}</span>
              <span className="mx-2 text-orange-500">|</span>
              {rule.name}
            </h3>
          )}
          <IconButton
            size="small"
            onClick={handleClick}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            title="More options"
            disabled={disableActions}
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                minWidth: '150px',
                mt: 0.5,
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }
            }}
          >
            <MenuItem
              onClick={handleView}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                }
              }}
            >
              <VisibilityIcon fontSize="small" sx={{ mr: 1.5, color: '#3B82F6', fontSize: '16px' }} />
              View Details
            </MenuItem>
            <MenuItem
              onClick={handleTest}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.06)',
                }
              }}
            >
              <PlayArrowIcon fontSize="small" sx={{ mr: 1.5, color: '#10B981', fontSize: '16px' }} />
              Test Rule
            </MenuItem>
            <MenuItem
              onClick={handleEdit}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                }
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#3B82F6', fontSize: '16px' }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={handleDelete}
              sx={{
                fontSize: '13px',
                py: 1.25,
                color: '#EF4444',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.06)',
                }
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1.5, fontSize: '16px' }} />
              Delete
            </MenuItem>
          </Menu>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <Badge variant={rule.isActive ? 'success' : 'secondary'}>
            {rule.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <span className="text-xs text-gray-600 font-medium">Priority {rule.priority}</span>
        </div>

        <div className="space-y-2">
          <div className="bg-blue-50/40 rounded-md p-2 border border-blue-100/60">
            <div className="flex items-center gap-1.5 mb-1.5">
              <RouteIcon style={{ fontSize: '14px', color: '#3B82F6' }} />
              <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">
                Result Configuration
              </span>
            </div>
            <div className="text-xs">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-gray-700">Result Via</span>
                <Badge variant={getResultViaVariant(rule.resultVia)} className="text-[10px] font-semibold">
                  {rule.resultVia}
                </Badge>
                <span className="font-semibold text-gray-700">Result Data</span>
                {rule.resultData && shouldShowTooltip(rule.resultData) ? (
                  <Tooltip
                    title={rule.resultData}
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'white',
                          color: 'rgba(0, 0, 0, 0.87)',
                          fontSize: '0.75rem',
                          maxWidth: '200px',
                          padding: '8px 12px',
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        }
                      },
                      arrow: {
                        sx: {
                          color: 'white',
                        }
                      }
                    }}
                  >
                    <span className="text-gray-800 font-medium truncate block cursor-help">
                      {rule.resultData}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="text-gray-800 font-medium truncate block">
                    {rule.resultData || '—'}
                  </span>
                )}
              </div>
            </div>
          </div>


          <div className="bg-purple-50/40 rounded-md p-2 border border-purple-100/60">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <FilterListIcon style={{ fontSize: '14px', color: '#9333EA' }} />
                <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Conditions</span>
              </div>
              <span className="text-[10px] font-bold text-purple-700 bg-purple-100/60 px-1.5 py-0.5 rounded-md border border-purple-200/60">
                {(rule.conditions || []).length} {(rule.conditions || []).length !== 1 ? 'items' : 'item'}
              </span>
            </div>

            <div className="mt-1.5 space-y-1.5">
              {rule.conditions && rule.conditions.length > 0 ? (
                <ul className="list-none space-y-1.5">
                  {rule.conditions?.map((condition: any, index: number) => (
                    <li
                      key={`${rule.id}-cond-${index}`}
                      className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primaryColor flex-shrink-0"></span>
                      <span className="font-bold text-gray-900 uppercase break-words">{condition.param}</span>
                      <span className="font-black italic text-purple-800 font-mono text-xs flex-shrink-0">{condition.operator}</span>
                      <span className="font-semibold text-primaryColor uppercase break-words">{condition.value}</span>
                      {condition.cast && condition.cast !== 'string' && (
                        <span className="text-gray-500 text-[10px] bg-gray-100 px-1 py-0.5 rounded font-medium">({condition.cast})</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-gray-400 bg-white/60 p-2 rounded-md border border-dashed border-gray-200 font-medium">
                  No conditions defined
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-gray-200 mt-3 p-3 bg-gray-50/50">
        <FormControlLabel
          control={
            <Switch
              checked={rule.isActive}
              onChange={() => onToggle(rule)}
              color="primary"
              size="small"
              disabled={disableActions}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#10B981',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#10B981',
                },
              }}
            />
          }
          label={
            <span className="text-xs font-medium text-gray-700">
              {rule.isActive ? 'Active' : 'Inactive'}
            </span>
          }
          sx={{
            margin: 0,
            '& .MuiFormControlLabel-label': {
              marginLeft: '8px',
            },
          }}
        />
      </div>
      {isDeleting && (
        <div className="absolute inset-0 bg-white/90 backdrop-blur-[2px] flex items-center justify-center gap-2 text-xs font-medium text-gray-500">
          <CircularProgress size={16} sx={{ color: '#3B82F6' }} />
          Deleting…
        </div>
      )}
    </div>
  );
};

